import { Button, type ButtonProps } from '@Pages/pageRuntime/components/ui/Button';
import { useTheme } from '@Pages/pageRuntime/hooks/useTheme';
import type { SdkThemeMode } from '@Pages/pageRuntime/hooks/useTheme';
import MoonIcon from 'lucide-react/dist/esm/icons/moon';
import SunIcon from 'lucide-react/dist/esm/icons/sun';
import SunMoonIcon from 'lucide-react/dist/esm/icons/sun-moon';

type ThemeToggleProps = {
  /** Additional classes for the button. */
  className?: string;

  /** Button visual style. */
  variant?: ButtonProps['variant'];

  /** Button size preset. */
  size?: ButtonProps['size'];
};

const themeLabels: Record<SdkThemeMode, string> = {
  system: 'System theme',
  dark: 'Dark theme',
  light: 'Light theme'
};

const themeIcons: Record<SdkThemeMode, typeof SunIcon> = {
  system: SunMoonIcon,
  dark: MoonIcon,
  light: SunIcon
};

/**
 * Icon button that cycles the app theme through System → Dark → Light. Shows the icon
 * and tooltip for the current mode; the choice persists across visits. Reach for
 * `Sdk.hooks.useTheme` instead when the page needs custom theme UI (a labeled menu item,
 * a light/dark/system picker, …).
 *
 * @example
 * <Sdk.Components.ThemeToggle />
 */
export function ThemeToggle({ className, variant = 'ghost', size = 'icon' }: ThemeToggleProps) {
  const { mode, toggleTheme } = useTheme();
  const ModeIcon = themeIcons[mode];
  const label = themeLabels[mode];

  return (
    <Button
      aria-label={label}
      tooltip={label}
      className={className}
      variant={variant}
      size={size}
      onClick={toggleTheme}
    >
      <ModeIcon />
    </Button>
  );
}
